import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";

// Icons
import {
  Dashboard as DashboardIcon,
  AttachMoney as GSTIcon,
  Receipt as InvoiceIcon,
  Assignment as TaskIcon,
  AccountBalance as ReturnIcon,
  PeopleAlt as ClientIcon,
  AssessmentOutlined as ReportIcon,
  PersonAdd as OnboardIcon,
  CloudUpload as UploadIcon,
  CreditCard as PaymentIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Menu as MenuIcon,
} from "@mui/icons-material";
import CreditCardIcon from "@mui/icons-material/CreditCard";

const GSTPortalSidebar = ({ forceCollapsed = false }) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeMenu, setActiveMenu] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (forceCollapsed) {
      setCollapsed(true);
    } else if (isSmallScreen || isMobile) {
      setCollapsed(true);
    } else if (isTablet) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [forceCollapsed, isSmallScreen, isMobile, isTablet]);

  const handleDrawerToggle = () => {
    if (!isMobile && !forceCollapsed) {
      setCollapsed(!collapsed);
    }
  };

  const handleMenuClick = (index) => {
    if (!collapsed) {
      setActiveMenu(activeMenu === index ? null : index);
    }
  };

  const drawerWidth = collapsed ? 72 : 280;

  // GST Portal menu items with their sub-items
  const menuItems = [
    {
      id: 1,
      title: "Dashboard",
      icon: <DashboardIcon />,
      items: [
        { name: "Overview", path: "/GstPortal" },
      ]
    },
    {
      id: 2,
      title: "GST Returns",
      icon: <ReturnIcon />,
      items: [
        { name: "GSTR-1 (Outward Supplies)", path: "/Gstrone" },
        { name: "GSTR-2 (Inward Supplies)", path: "/GstTwo" },
        { name: "GSTR-3B (Monthly Return)", path: "/Gstthree" },
        { name: "Annual Return Filing", path: "/GstAnnual" },
      ]
    },
    {
      id: 3,
      title: "Invoicing",
      icon: <InvoiceIcon />,
      items: [
        { name: "Generate Invoices", path: "/invoicemanagement" },
        { name: "Invoice Repository", path: "/invoiceDocuments" },
        { name: "Payment Tracking", path: "/GstPaymentTracking" },
      ]
    },
    {
      id: 4,
      title: "Ewaybill",
      icon: <CreditCardIcon />,
      items: [
        { name: "Ewaybill Management", path: "/Ewaybills" },
        { name: "Ewaybills", path: "/Ewaybill" },
      ]
    },
    {
      id: 5,
      title: "Clients",
      icon: <ClientIcon />,
      items: [
        { name: "Client Onboarding", path: "/kycForm" },
        { name: "Client Directory", path: "/GstclientDirectory" },
        { name: "Client Tasks", path: "/GstClientTask" },
      ]
    },
    {
      id: 6,
      title: "Task Management",
      icon: <TaskIcon />,
      items: [
        { name: "Active Tasks", path: "/GstActiveTask" },
        { name: "Task Assignment", path: "/GstAssignmentManagement" },
        { name: "Task Calendar", path: "/GstTaskCalender" },
      ]
    },
    {
      id: 7,
      title: "Compliance",
      icon: <GSTIcon />,
      items: [
        { name: "Compliance Checklist", path: "/GstcompliceChecklist" },
        { name: "Pending Compliances", path: "/Gstpendingcomplice" },
        { name: "Deadline Tracker", path: "/Gstcomplicedeadline" },
      ]
    },
    {
      id: 8,
      title: "Reporting",
      icon: <ReportIcon />,
      items: [
        { name: "Tax Reports", path: "/GstTaxReport" },
        { name: "Compliance Reports", path: "/GSTComplianceReport" },
        { name: "Client Performance", path: "/GstclientReports" },
      ]
    },
  ];

  const renderMenuItem = (item) => (
    <Box key={item.id}>
      <ListItemButton
        onClick={() => handleMenuClick(item.id)}
        selected={activeMenu === item.id}
        sx={{
          minHeight: 48,
          px: collapsed ? 1.5 : 2.5,
          margin: "4px 16px",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "#FFF6E9",
            transform: "translateX(4px)",
            "& .MuiListItemText-primary": {
              color: "#EB5A3C",
              fontWeight: "bold",
            },
            "& .MuiListItemIcon-root": {
              color: "#EB5A3C",
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : 3,
            justifyContent: "center",
            color: theme.palette.primary.main,
          }}
        >
          <Tooltip
            title={item.title}
            placement="right"
            disableHoverListener={!collapsed}
          >
            {item.icon}
          </Tooltip>
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText
              primary={item.title}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                },
              }}
            />
            {activeMenu === item.id ? (
              <KeyboardArrowRight />
            ) : (
              <KeyboardArrowDown />
            )}
          </>
        )}
      </ListItemButton>

      {!collapsed && (
        <Collapse in={activeMenu === item.id} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.items.map((subItem, index) => (
              <ListItemButton
                key={index}
                component={Link}
                to={subItem.path}
                sx={{
                  pl: 4,
                  margin: "2px 16px",
                  borderRadius: "8px",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: location.pathname === subItem.path ? "#FFF6E9" : "transparent",
                  "&:hover": {
                    backgroundColor: "#FFF6E9",
                    transform: "translateX(4px)",
                    "& .MuiListItemText-primary": {
                      color: "#EB5A3C",
                      fontWeight: "bold",
                    },
                  },
                  "& .MuiListItemText-primary": {
                    color: location.pathname === subItem.path ? "#EB5A3C" : "inherit",
                    fontWeight: location.pathname === subItem.path ? "bold" : "normal",
                  },
                }}
              >
                <ListItemText
                  primary={subItem.name}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Box
                     sx={{
                       display: "flex",
                       alignItems: "center",
                       justifyContent: "center",
                     }}
                   >
                   
                     <Typography
                       variant="h6"
                       component="span"
                       sx={{
                         color: "black",
                         fontSize: "1.5rem",
                         fontWeight: "bold",
                       }}
                     >
                       GST
                     </Typography>
                     <Typography
                       variant="h6"
                       component="span"
                       sx={{
                         color: "#D32F2F",
                         fontSize: "1.5rem",
                         fontWeight: "bold",
                          ml: 1,
                       }}
                     >
                       Portal
                     </Typography>
                   </Box>
        )}
        {!isMobile && !forceCollapsed && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <List>
          {menuItems.map(renderMenuItem)}
        </List>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: {
            xs: 120,
            sm: 140,
            md: collapsed ? 100 : 160,
            lg: 180,
          },
          flexShrink: 0,
          backgroundColor: "#fafafa",
        }}
      >
        <Box
          component="img"
          src="/icon.jpg"
          alt="Company Logo"
          sx={{
            height: {
              xs: collapsed ? 40 : 60,
              sm: collapsed ? 50 : 70,
              md: collapsed ? 60 : 90,
              lg: collapsed ? 70 : 120,
            },
            width: "auto",
            objectFit: "contain",
            maxWidth: {
              xs: collapsed ? "33px" : "148px",
              sm: collapsed ? "38px" : "178px",
              md: collapsed ? "43px" : "198px",
              lg: collapsed ? "48px" : "218px",
            },
            display: "block",
            mt: { xs: 0.5, sm: 1 },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <motion.div
      animate={{ width: drawerWidth }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{
        width: drawerWidth,
        flexShrink: 0,
        height: "100%",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: drawerWidth,
          height: "100%",
          borderRight: "1px solid #e0e0e0",
          backgroundColor: "#fff",
          overflow: "hidden",
          position: "fixed",
          top: 64,
          left: 0,
          bottom: 0,
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: !collapsed ? "3px" : "0px",
            backgroundColor: theme.palette.primary.main,
            transition: "width 0.3s ease-in-out",
            zIndex: 1,
          },
        }}
      >
        {drawer}
      </Box>
    </motion.div>
  );
};

export default GSTPortalSidebar;
